import { useCallback, useEffect, useRef, useState } from 'react'
import { ImagePlus, Loader2, X, GripVertical } from 'lucide-react'
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/hooks/useAuth'
import { useLanguage } from '@/context/LanguageContext'

/* eslint-disable @typescript-eslint/no-explicit-any */
const sb = supabase as any

interface FallbackImage {
  id: string
  image_url: string
  position: number
}

interface DndGalleryManagerProps {
  images: FallbackImage[]
  deletingId: string | null
  onDelete: (img: FallbackImage) => void
  onReorder: (orderedIds: string[]) => void
  it: boolean
}

function DndGalleryManager({ images, deletingId, onDelete, onReorder, it }: DndGalleryManagerProps) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const ids = images.map((i) => i.id)
    const oldIndex = ids.indexOf(String(active.id))
    const newIndex = ids.indexOf(String(over.id))
    if (oldIndex === -1 || newIndex === -1) return
    onReorder(arrayMove(ids, oldIndex, newIndex))
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={images.map((i) => i.id)} strategy={rectSortingStrategy}>
        <div className="mt-5 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
          {images.map((img) => (
            <SortableThumb key={img.id} id={img.id}>
              <img
                src={img.image_url}
                alt=""
                draggable={false}
                className="pointer-events-none w-full h-full object-cover"
                loading="lazy"
              />
              <button
                type="button"
                disabled={deletingId === img.id}
                onClick={() => onDelete(img)}
                aria-label={it ? 'Elimina immagine' : 'Delete image'}
                className="absolute top-1.5 right-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-gray-700 shadow transition hover:bg-red-500 hover:text-white disabled:opacity-60"
              >
                {deletingId === img.id
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <X className="h-4 w-4" />}
              </button>
            </SortableThumb>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  )
}

const BUCKET = 'homepage-gallery'

function SortableThumb({ id, children }: { id: string; children: React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id })

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      title="Trascina per riordinare"
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`relative aspect-square cursor-grab touch-none rounded-xl overflow-hidden bg-gray-100 group select-none active:cursor-grabbing ${isDragging ? 'z-20 opacity-80' : ''}`}
    >
      <div className="absolute bottom-1.5 left-1.5 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-gray-400 shadow">
        <GripVertical className="h-4 w-4" />
      </div>
      {children}
    </div>
  )
}

function extractStoragePath(publicUrl: string): string | null {
  const marker = `/object/public/${BUCKET}/`
  const idx = publicUrl.indexOf(marker)
  if (idx === -1) return null
  return publicUrl.slice(idx + marker.length)
}

export default function HomepageGalleryManager() {
  const { user } = useAuth()
  const { language } = useLanguage()
  const it = language === 'it'

  const [isAdmin, setIsAdmin] = useState(false)
  const [checked, setChecked] = useState(false)
  const [images, setImages] = useState<FallbackImage[]>([])
  const [prioritize, setPrioritize] = useState(true)
  const [savingPriority, setSavingPriority] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!user) { setIsAdmin(false); return }
    sb.rpc('has_role', { _user_id: user.id, _role: 'admin' })
      .then(({ data }: { data: boolean | null }) => setIsAdmin(!!data))
  }, [user?.id])

  const loadData = useCallback(async () => {
    setError(null)
    const [settingsRes, imagesRes] = await Promise.all([
      sb.from('homepage_gallery_settings').select('prioritize_real_images').eq('id', 1).maybeSingle(),
      sb.from('homepage_fallback_images').select('id, image_url, position').order('position', { ascending: true }),
    ])
    if (settingsRes.error) { setError(settingsRes.error.message); return }
    if (imagesRes.error) { setError(imagesRes.error.message); return }
    setPrioritize(settingsRes.data?.prioritize_real_images ?? true)
    setChecked(true)
    setImages((imagesRes.data ?? []) as FallbackImage[])
  }, [])

  useEffect(() => {
    if (isAdmin) loadData()
  }, [isAdmin, loadData])

  const handleTogglePriority = async (next: boolean) => {
    setSavingPriority(true)
    setError(null)
    const { error: err } = await sb
      .from('homepage_gallery_settings')
      .update({ prioritize_real_images: next })
      .eq('id', 1)
    setSavingPriority(false)
    if (err) { setError(err.message); return }
    setPrioritize(next)
  }

  const handleUpload = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return
    setUploading(true)
    setError(null)
    try {
      const { data: maxRow, error: maxErr } = await sb
        .from('homepage_fallback_images')
        .select('position')
        .order('position', { ascending: false })
        .limit(1)
        .maybeSingle()
      if (maxErr) throw new Error(maxErr.message)
      let position = (maxRow?.position ?? 0) + 1

      for (const file of Array.from(fileList)) {
        const path = `fallback/${crypto.randomUUID()}-${file.name}`
        const { error: upErr } = await sb.storage.from(BUCKET).upload(path, file)
        if (upErr) throw new Error(upErr.message)
        const { data: urlData } = sb.storage.from(BUCKET).getPublicUrl(path)
        const publicUrl = urlData?.publicUrl
        if (!publicUrl) throw new Error('Unable to resolve public URL')
        const { error: insErr } = await sb
          .from('homepage_fallback_images')
          .insert({ image_url: publicUrl, position })
        if (insErr) {
          await sb.storage.from(BUCKET).remove([path])
          throw new Error(insErr.message)
        }
        position++
      }
      await loadData()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleDelete = async (img: FallbackImage) => {
    setDeletingId(img.id)
    setError(null)
    try {
      const path = extractStoragePath(img.image_url)
      if (path) {
        const { error: rmErr } = await sb.storage.from(BUCKET).remove([path])
        if (rmErr) throw new Error(rmErr.message)
      }
      const { error: delErr } = await sb
        .from('homepage_fallback_images')
        .delete()
        .eq('id', img.id)
      if (delErr) throw new Error(delErr.message)
      setImages((prev) => prev.filter((i) => i.id !== img.id))
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setDeletingId(null)
    }
  }

  const handleReorder = async (orderedIds: string[]) => {
    const reordered = orderedIds
      .map((id) => images.find((i) => i.id === id))
      .filter((i): i is FallbackImage => !!i)
    if (reordered.length === 0) return
    setImages(reordered)
    setError(null)
    // Solo UPDATE (non upsert): l'upsert richiederebbe anche la policy di INSERT.
    for (let index = 0; index < reordered.length; index++) {
      const { error: err } = await sb
        .from('homepage_fallback_images')
        .update({ position: index })
        .eq('id', reordered[index].id)
      if (err) {
        setError(err.message)
        loadData()
        return
      }
    }
  }

  if (!isAdmin || !checked) return null

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 sm:p-6">
      <h3 className="text-lg font-semibold text-gray-900">Gallery Homepage</h3>
      <p className="mt-1 text-sm text-gray-500">
        {it
          ? 'Queste immagini vengono mostrate in homepage quando la galleria reale non è disponibile.'
          : 'These images are shown on the homepage when the real gallery is unavailable.'}
      </p>

      <label className="mt-4 flex items-start gap-3 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={prioritize}
          disabled={savingPriority}
          onChange={(e) => handleTogglePriority(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400 accent-rose-500"
        />
        <span className="text-sm text-gray-700">
          {it
            ? 'Dai priorità alle immagini vere provenienti dagli ultimi articoli caricati nelle bacheche pubbliche degli utenti.'
            : 'Prioritize real images containing the latest public items uploaded by users'}
        </span>
        {savingPriority && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
      </label>

      <div className="mt-5">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          onChange={(e) => handleUpload(e.target.files)}
          className="hidden"
          id="homepage-gallery-upload"
        />
        <button
          type="button"
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
          className="inline-flex items-center gap-2 rounded-xl bg-rose-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-rose-600 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {uploading
            ? <Loader2 className="h-4 w-4 animate-spin" />
            : <ImagePlus className="h-4 w-4" />}
          {uploading
            ? (it ? 'Caricamento…' : 'Uploading…')
            : (it ? 'Carica immagini' : 'Upload images')}
        </button>
      </div>

      {error && (
        <p className="mt-3 text-sm text-red-600">{error}</p>
      )}

      {images.length > 0 && (
        <DndGalleryManager
          images={images}
          deletingId={deletingId}
          onDelete={handleDelete}
          onReorder={handleReorder}
          it={it}
        />
      )}
    </div>
  )
}
